import React, { SetStateAction, useCallback, useMemo } from 'react';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import {
  fetchSavedSearch,
  createSavedSearch,
  updateSavedSearch,
} from 'talent_matching/api';
import { useUserContext } from 'talent_matching/contexts/user_context';
import SavedFilterButtons from 'talent_matching/components/filters/saved_filter_buttons';
import LoadFiltersModal from 'talent_matching/talent_rediscovery/components/filters/load_filters_modal';
import SaveFiltersModal from 'talent_search/talent_rediscovery/modals/save_filters_modal';

export type SavedFiltersProps = {
  featureType: string;
  savedFilterSet: Item | null;
  setSavedFilterSet: (item: Item | null) => void;
  setSavedSearchOptions: (options: SetStateAction<Item[]>) => void;
  formDefaults: Record<string, unknown>;
  savedSearchOptions: Item[];
  parseSavedSearch: (response: unknown) => Record<string, unknown>;
  buildQueryString: () => string;
};

const SavedFilters = ({
  featureType,
  savedFilterSet,
  setSavedFilterSet,
  setSavedSearchOptions,
  formDefaults,
  savedSearchOptions,
  parseSavedSearch,
  buildQueryString,
}: SavedFiltersProps) => {
  const { reset, setValue } = useFormContext();
  const {
    paths: { savedSearchesPath },
  } = useUserContext();

  const filterNames = useMemo(
    () => savedSearchOptions.map((o) => o.label),
    [savedSearchOptions]
  );
  const [loadModalOpen, openLoadModal, closeLoadModal] = useModal();
  const [saveModalOpen, openSaveModal, closeSaveModal] = useModal();

  const handleLoadSelect = useCallback(
    async (option: Item | null) => {
      setSavedFilterSet(option);
      if (!option) {
        reset(formDefaults);
        return;
      }

      const response = await fetchSavedSearch(
        savedSearchesPath,
        option.value as unknown as number,
        featureType
      );
      const formFields = parseSavedSearch(response);
      reset(formDefaults);
      Object.keys(formFields).forEach((key) => {
        setValue(key, formFields[key], { shouldDirty: true });
      });
    },
    [
      setSavedFilterSet,
      formDefaults,
      savedSearchesPath,
      featureType,
      parseSavedSearch,
      reset,
      setValue,
    ]
  );

  const handleSaveFilters = useCallback(
    async (name: string) => {
      const response = await createSavedSearch(
        savedSearchesPath,
        buildQueryString(),
        name,
        featureType
      );
      const newOption = {
        value: response.id.toString(),
        label: response.label,
      };
      setSavedSearchOptions((prev: Item[]) => [newOption, ...prev]);
      setSavedFilterSet(newOption);
      closeSaveModal();
    },
    [
      savedSearchesPath,
      buildQueryString,
      featureType,
      setSavedSearchOptions,
      setSavedFilterSet,
      closeSaveModal,
    ]
  );

  const handleUpdateFilters = useCallback(async () => {
    if (!savedFilterSet?.value) {
      return;
    }
    await updateSavedSearch(
      savedSearchesPath,
      savedFilterSet.value as unknown as number,
      buildQueryString(),
      savedFilterSet.label,
      featureType
    );
  }, [savedSearchesPath, savedFilterSet, buildQueryString, featureType]);

  return (
    <>
      <SavedFilterButtons
        savedFilterSet={savedFilterSet}
        onLoadFilters={openLoadModal}
        onSaveFilters={openSaveModal}
        onUpdateFilters={handleUpdateFilters}
      />
      <LoadFiltersModal
        isOpen={loadModalOpen}
        onDismiss={closeLoadModal}
        options={savedSearchOptions}
        onSelect={handleLoadSelect}
      />
      <SaveFiltersModal
        isOpen={saveModalOpen}
        close={closeSaveModal}
        onSave={handleSaveFilters}
        filterNames={filterNames}
      />
    </>
  );
};

export default SavedFilters;
